import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { employeeAPI, reviewsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import VettScoreCard from '../../components/common/VettScoreCard';
import QuickActionsWidget from '../../components/dashboard/QuickActionsWidget';
import { 
  FileText, CheckCircle, Clock, Eye, User, MessageSquare, Settings,
  ChevronRight, Loader2, Calendar, AlertTriangle, Star, Copy, Share2, Shield,
  Globe, Users, EyeOff, Building2, XCircle, X
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [profile, setProfile] = useState(null);
  const [scheduledReviews, setScheduledReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileVisibility, setProfileVisibility] = useState('open');
  const [pendingAccessRequests, setPendingAccessRequests] = useState([]);
  const [accessApprovals, setAccessApprovals] = useState([]);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    checkOnboardingAndFetchData();
    fetchVisibilityData();
  }, []);

  const fetchVisibilityData = async () => {
    try {
      const [visRes, requestsRes, approvalsRes] = await Promise.all([
        employeeAPI.getProfileVisibility(),
        employeeAPI.getAccessRequests(),
        employeeAPI.getAccessApprovals()
      ]);
      setProfileVisibility(visRes.data.visibility || 'open');
      setPendingAccessRequests(requestsRes.data.requests || []);
      setAccessApprovals(approvalsRes.data.approvals || []);
    } catch (error) {
      console.error('Error fetching visibility data:', error);
    }
  };

  const handleRespondToRequest = async (requestId, action) => {
    setProcessingId(requestId);
    try {
      await employeeAPI.respondToAccessRequest(requestId, action);
      toast.success(action === 'approve' ? 'Access approved' : 'Request declined');
      fetchVisibilityData(); // Refresh data
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRevokeAccess = async (employerId) => {
    setProcessingId(employerId);
    try {
      await employeeAPI.revokeAccessApproval(employerId);
      setAccessApprovals(accessApprovals.filter(a => a.employer_id !== employerId));
      toast.success('Access revoked');
    } catch (error) {
      toast.error('Failed to revoke access');
    } finally {
      setProcessingId(null);
    }
  };

  const checkOnboardingAndFetchData = async () => {
    try {
      // First check if onboarding is complete
      const profileRes = await employeeAPI.getProfile();
      if (!profileRes.data.onboarding_complete) {
        navigate('/employee/onboarding', { replace: true });
        return;
      }
      
      setProfile(profileRes.data);
      
      // Fetch remaining data
      const [statsRes, activityRes, scheduledRes] = await Promise.all([
        employeeAPI.getDashboardStats(),
        employeeAPI.getActivity(),
        reviewsAPI.getEmployeeScheduledReviews().catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data);
      setScheduledReviews(scheduledRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { label: 'Total Records', value: stats.total_records, icon: FileText, color: 'text-blue-400' },
    { label: 'Verified Records', value: stats.verified_records, icon: CheckCircle, color: 'text-green-400' },
    { label: 'Pending Review', value: stats.pending_records, icon: Clock, color: 'text-amber-400' },
    { label: 'Profile Views', value: stats.profile_views, icon: Eye, color: 'text-purple-400' },
  ] : [];

  const quickActions = [
    { label: 'Edit Profile', href: '/employee/profile', icon: User },
    { label: 'View My Records', href: '/employee/records', icon: FileText },
    { label: 'Reviews', href: '/employee/reviews', icon: Star },
    { label: 'Messages', href: '/employee/messages', icon: MessageSquare },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="employee-dashboard">
        {/* Welcome Header with VETT ID */}
        <div className="animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">
                Welcome back{profile?.first_name ? `, ${profile.first_name}` : ''}!
              </h1>
              <p className="mt-2 text-[#5B6472] text-sm sm:text-base">Here's an overview of your VETT profile.</p>
            </div>
            
            {/* VETT ID Card */}
            {profile?.vett_id && (
              <Card className="bg-gradient-to-r from-[#1B5AFF]/20 to-[#1B5AFF]/5 border border-[#1B5AFF]/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#1B5AFF]/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#1B5AFF]" />
                  </div>
                  <div>
                    <p className="text-[#5B6472] text-xs">Your VETT ID</p>
                    <p className="text-[#0A0F1E] font-mono font-bold text-lg" data-testid="vett-id-display">{profile.vett_id}</p>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(profile.vett_id);
                        toast.success('VETT ID copied!');
                      }}
                      className="h-8 w-8 p-0 text-[#5B6472] hover:text-[#1B5AFF] hover:bg-[#1B5AFF]/10"
                      title="Copy VETT ID"
                      data-testid="copy-vett-id-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const shareText = `View my verified professional profile: vettpro.org/profile/${profile.vett_id}`;
                        navigator.clipboard.writeText(shareText);
                        toast.success('Share link copied!');
                      }}
                      className="h-8 w-8 p-0 text-[#5B6472] hover:text-[#1B5AFF] hover:bg-[#1B5AFF]/10"
                      title="Share profile"
                      data-testid="share-vett-id-btn"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Profile Visibility Status Indicator */}
        <Link to="/employee/settings" className="block">
          <Card 
            className={`border rounded-xl p-4 transition-all hover:border-opacity-70 cursor-pointer ${
              profileVisibility === 'open' 
                ? 'bg-green-500/5 border-green-500/30 hover:bg-green-500/10'
                : profileVisibility === 'selective'
                ? 'bg-blue-500/5 border-blue-500/30 hover:bg-blue-500/10'
                : 'bg-amber-500/5 border-amber-500/30 hover:bg-amber-500/10'
            }`}
            data-testid="visibility-indicator"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  profileVisibility === 'open' 
                    ? 'bg-green-500/20'
                    : profileVisibility === 'selective'
                    ? 'bg-blue-500/20'
                    : 'bg-amber-500/20'
                }`}>
                  {profileVisibility === 'open' ? (
                    <Globe className="w-5 h-5 text-green-400" />
                  ) : profileVisibility === 'selective' ? (
                    <Users className="w-5 h-5 text-blue-400" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-amber-400" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${
                      profileVisibility === 'open' 
                        ? 'text-green-400'
                        : profileVisibility === 'selective'
                        ? 'text-blue-400'
                        : 'text-amber-400'
                    }`}>
                      Profile: {profileVisibility.charAt(0).toUpperCase() + profileVisibility.slice(1)}
                    </span>
                    {pendingAccessRequests.length > 0 && profileVisibility === 'selective' && (
                      <Badge className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] border-0 text-xs">
                        {pendingAccessRequests.length} request{pendingAccessRequests.length > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                  <p className="text-[#5B6472] text-sm">
                    {profileVisibility === 'open' && 'Visible to all verified employers'}
                    {profileVisibility === 'selective' && 'Limited info visible, full access by approval'}
                    {profileVisibility === 'private' && 'Hidden from all searches'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#5B6472] text-sm hidden sm:inline">Change</span>
                <ChevronRight className="w-5 h-5 text-[#5B6472]" />
              </div>
            </div>
          </Card>
        </Link>

        {/* Access Requests Section - Show when selective visibility and has requests or approvals */}
        {profileVisibility === 'selective' && (pendingAccessRequests.length > 0 || accessApprovals.length > 0) && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden animate-fade-in" data-testid="access-requests-section">
            {/* Pending Access Requests */}
            {pendingAccessRequests.length > 0 && (
              <div className="p-6 border-b border-[#E8EDF5]">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#1B5AFF]/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#1B5AFF]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#0A0F1E]">Access Requests</h2>
                    <p className="text-[#5B6472] text-sm">Employers requesting access to your full records</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {pendingAccessRequests.map((request) => (
                    <div
                      key={request.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]"
                      data-testid={`access-request-${request.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#F5F8FF] flex items-center justify-center flex-shrink-0">
                          {request.company_logo ? (
                            <img src={request.company_logo} alt="" className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <Building2 className="w-6 h-6 text-[#5B6472]" />
                          )}
                        </div>
                        <div>
                          <p className="text-[#0A0F1E] font-medium">{request.company_name}</p>
                          <p className="text-[#5B6472] text-sm">
                            Requested {new Date(request.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 sm:flex-shrink-0">
                        <Button
                          onClick={() => handleRespondToRequest(request.id, 'decline')}
                          disabled={processingId === request.id}
                          variant="outline"
                          size="sm"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-lg"
                          data-testid={`decline-request-${request.id}`}
                        >
                          {processingId === request.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-1" />
                              Decline
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleRespondToRequest(request.id, 'approve')}
                          disabled={processingId === request.id}
                          size="sm"
                          className="bg-green-500 hover:bg-green-600 hover:bg-green-700 text-[#FFFFFF] rounded-lg"
                          data-testid={`approve-request-${request.id}`}
                        >
                          {processingId === request.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approved Access */}
            {accessApprovals.length > 0 && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#0A0F1E]">Approved Access</h2>
                    <p className="text-[#5B6472] text-sm">Employers with full access to your records</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {accessApprovals.map((approval) => (
                    <div
                      key={approval.employer_id}
                      className="flex items-center justify-between p-3 rounded-xl bg-[#F5F8FF]/30 border border-[#E8EDF5] hover:bg-[#F5F8FF]/50 transition-colors"
                      data-testid={`approved-access-${approval.employer_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#F5F8FF] flex items-center justify-center">
                          {approval.company_logo ? (
                            <img src={approval.company_logo} alt="" className="w-8 h-8 rounded-md object-cover" />
                          ) : (
                            <Building2 className="w-5 h-5 text-[#5B6472]" />
                          )}
                        </div>
                        <div>
                          <p className="text-[#0A0F1E] font-medium text-sm">{approval.company_name}</p>
                          <p className="text-[#5B6472] text-xs">
                            Approved {new Date(approval.approved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeAccess(approval.employer_id)}
                        disabled={processingId === approval.employer_id}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg h-8"
                        data-testid={`revoke-access-${approval.employer_id}`}
                      >
                        {processingId === approval.employer_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <X className="w-4 h-4 mr-1" />
                            Revoke
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* VETT Score Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1">
            <VettScoreCard isPro={profile?.is_pro || false} />
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 gap-3 sm:gap-4">
            {statCards.map((stat, i) => (
              <Card 
                key={stat.label} 
                className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-5 animate-fade-in"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="text-[#5B6472] text-xs sm:text-sm">{stat.label}</p>
                    <p className="text-xl sm:text-2xl font-bold text-[#0A0F1E] mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#F5F8FF] flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Quick Actions Widget */}
        <QuickActionsWidget userType="employee" />

        {/* Scheduled Reviews Section */}
        {scheduledReviews.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[#0A0F1E] flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1B5AFF]" />
                Upcoming Reviews
              </h2>
              <Link to="/employee/reviews">
                <Button variant="ghost" className="text-[#1B5AFF] hover:bg-[#1B5AFF]/10 rounded-xl text-sm">
                  View All
                </Button>
              </Link>
            </div>
            <div className="space-y-3">
              {scheduledReviews.slice(0, 3).map((review) => {
                const isOverdue = review.status === 'overdue';
                const dueDate = new Date(review.due_date);
                const reviewTypeColors = {
                  'weekly': 'bg-green-500/20 text-green-400 border-green-500/30',
                  'quarterly': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                  'annual': 'bg-red-500/20 text-red-400 border-red-500/30',
                  'one-time': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                };
                
                return (
                  <div 
                    key={review.id}
                    className={`p-4 rounded-xl border ${
                      isOverdue ? 'bg-red-500/10 border-red-500/30' : 'bg-[#F5F8FF]/30 border-[#E8EDF5]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isOverdue ? 'bg-red-500/20' : 'bg-[#F5F8FF]'
                        }`}>
                          {isOverdue ? (
                            <AlertTriangle className="w-5 h-5 text-red-400" />
                          ) : (
                            <Calendar className="w-5 h-5 text-[#5B6472]" />
                          )}
                        </div>
                        <div>
                          <p className={`font-medium ${isOverdue ? 'text-red-400' : 'text-[#0A0F1E]'}`}>
                            {review.employer_name || 'Employer'}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs border ${reviewTypeColors[review.review_type] || reviewTypeColors['one-time']}`}>
                              {review.review_type?.charAt(0).toUpperCase() + review.review_type?.slice(1)}
                            </span>
                            <span className={`text-xs ${isOverdue ? 'text-red-400' : 'text-[#5B6472]'}`}>
                              {isOverdue ? 'Overdue' : dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      </div>
                      {review.focus_areas && (
                        <p className="text-[#5B6472] text-xs max-w-[150px] truncate hidden sm:block">
                          {review.focus_areas}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Recent Activity */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-xl font-semibold text-[#0A0F1E] mb-4">Recent Activity</h2>
            {activity.length > 0 ? (
              <div className="space-y-4">
                {activity.map((item, i) => (
                  <div key={item.id || i} className="flex items-start gap-3 pb-4 border-b border-[#E8EDF5] last:border-0 last:pb-0">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      item.type === 'record_submitted' ? 'bg-blue-400' :
                      item.type === 'record_verified' ? 'bg-green-400' :
                      item.type === 'profile_viewed' ? 'bg-purple-400' :
                      'bg-[#1B5AFF]'
                    }`} />
                    <div className="flex-1">
                      <p className="text-[#0A0F1E] text-sm">{item.message}</p>
                      <p className="text-[#5B6472] text-xs mt-1">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#5B6472] text-sm">No recent activity yet.</p>
            )}
          </Card>

          {/* Quick Actions */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-xl font-semibold text-[#0A0F1E] mb-4">Quick Actions</h2>
            <div className="space-y-3">
              {quickActions.map((action) => (
                <Link
                  key={action.href}
                  to={action.href}
                  className="flex items-center justify-between p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5] hover:border-[#1B5AFF]/50 transition-colors group"
                  data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <action.icon className="h-5 w-5 text-[#1B5AFF]" />
                    <span className="text-[#0A0F1E] font-medium">{action.label}</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[#5B6472] group-hover:text-[#1B5AFF] transition-colors" />
                </Link>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
